<script setup>
import { ref, computed, watch } from 'vue'
import Icon from '@/components/shell/Icon.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'
import { TERRITORIES } from '@/data/mockData.js'

const search = ref(''); const territoryFilter = ref('All'); const tierFilter = ref('All')
const toast = ref(null)
const notify = msg => { toast.value = msg; setTimeout(() => { toast.value = null }, 3200) }

// BUG FIX: this previously held a direct object snapshot (drawerPharmacy = p),
// the same staleness pattern OrderTracking.vue had before its Phase 1 fix.
// Recalculating a score or verifying OTP would mutate the store but the open
// drawer would keep showing the old values. Now it tracks the ID and looks
// the live object up reactively, same pattern as every other drawer since.
const drawerCustomerId = ref(null)
const drawerPharmacy = computed(() => drawerCustomerId.value ? store.data.pharmacies.value.find(p => p.customerId === drawerCustomerId.value) || null : null)

const territories = computed(() => ['All', ...new Set(store.data.pharmacies.value.map(p=>p.territory))].sort())
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return store.data.pharmacies.value.filter(p => {
    const ms = !q || p.pharmacyName.toLowerCase().includes(q) || p.customerId.toLowerCase().includes(q) || p.ownerName.toLowerCase().includes(q)
    return ms && (territoryFilter.value==='All'||p.territory===territoryFilter.value) && (tierFilter.value==='All'||p.potentialClassification===tierFilter.value)
  })
})
const drawerOrders = computed(() => !drawerPharmacy.value ? [] : store.data.orders.value.filter(o=>o.customerId===drawerPharmacy.value.customerId))
const drawerTotalValue = computed(() => drawerOrders.value.reduce((s,o)=>s+o.orderValue,0))
const openDrawer = p => { drawerCustomerId.value = p.customerId }

// Phase 3: land here from a global search result. Watcher, not onMounted —
// AppShell won't remount this component if the user searches again while
// already on Pharmacy Master.
watch(() => store.state.pendingFocus, f => {
  if (f && f.type === 'pharmacy') {
    const match = store.data.pharmacies.value.find(p => p.customerId === f.id)
    if (match) drawerCustomerId.value = match.customerId
    store.clearPendingFocus()
  }
}, { immediate: true })

// --- Phase 7: Add Pharmacy form + live duplicate check ---------------------
const showCreate = ref(false)
const blankCreateForm = () => ({ pharmacyName:'', ownerName:'', mobile1:'', mobile2:'', email1:'', drugLicenseNo:'', address:'', city:'', territory:'Harare', dailyFootfall:60, monthlyFootfall:1620 })
const createForm = ref(blankCreateForm())
const lastWarnedDuplicateId = ref(null)

const duplicateMatch = computed(() => store.findDuplicatePharmacy({ drugLicenseNo: createForm.value.drugLicenseNo, mobile1: createForm.value.mobile1 }))
watch(duplicateMatch, (match) => {
  if (match && lastWarnedDuplicateId.value !== match.customerId) {
    notify(`⚠ Possible duplicate: ${match.pharmacyName} (${match.customerId}) already uses this Drug License No. or Mobile 1.`)
    lastWarnedDuplicateId.value = match.customerId
  }
  if (!match) lastWarnedDuplicateId.value = null
})

function openCreate() { createForm.value = blankCreateForm(); showCreate.value = true }
function submitCreate() {
  if (duplicateMatch.value) return
  const created = store.createPharmacy({ ...createForm.value })
  if (created) {
    notify(`${created.customerId} — ${created.pharmacyName} onboarded. Status: Pending OTP.`)
    showCreate.value = false
    openDrawer(created)
  }
}

// --- Phase 7: Dual-OTP verification modal -----------------------------------
const showOtpModal = ref(false)
const otpInputs = ref({ mobile1:'', mobile2:'', email1:'', email2:'' })
function openOtpModal() { otpInputs.value = { mobile1:'', mobile2:'', email1:'', email2:'' }; showOtpModal.value = true }
const otpFieldsRequired = computed(() => {
  if (!drawerPharmacy.value) return []
  const p = drawerPharmacy.value
  const fields = [{ key:'mobile1', label:'Mobile 1', value:p.mobile1 }]
  if (p.mobile2) fields.push({ key:'mobile2', label:'Mobile 2', value:p.mobile2 })
  if (p.email1) fields.push({ key:'email1', label:'Email 1', value:p.email1 })
  if (p.email2) fields.push({ key:'email2', label:'Email 2', value:p.email2 })
  return fields
})
const otpAllFilled = computed(() => otpFieldsRequired.value.every(f => (otpInputs.value[f.key]||'').trim().length === 4))
function submitOtp() {
  if (!otpAllFilled.value || !drawerPharmacy.value) return
  store.verifyPharmacyOtp(drawerPharmacy.value.customerId)
  showOtpModal.value = false
  notify(`${drawerPharmacy.value.pharmacyName} verified and activated.`)
}

// --- Phase 7: Footfall + potential score recalculation ----------------------
const footfallForm = ref({ dailyFootfall: 0, monthlyFootfall: 0 })
watch(drawerPharmacy, p => { if (p) footfallForm.value = { dailyFootfall: p.dailyFootfall, monthlyFootfall: p.monthlyFootfall } }, { immediate: true })
function recalcScore() {
  if (!drawerPharmacy.value) return
  const before = drawerPharmacy.value.potentialClassification
  store.recalculatePotentialScore(drawerPharmacy.value.customerId, footfallForm.value.dailyFootfall, footfallForm.value.monthlyFootfall)
  const after = drawerPharmacy.value.potentialClassification
  notify(before === after ? `Score recalculated — still ${after}.` : `Score recalculated — moved from ${before} to ${after}.`)
}

// --- Phase 7: Field Visit Notes ---------------------------------------------
const noteInput = ref('')
const visitNotes = computed(() => drawerPharmacy.value ? (store.state.visitNotes[drawerPharmacy.value.customerId] || []) : [])
function submitNote() {
  if (!noteInput.value.trim() || !drawerPharmacy.value) return
  store.addVisitNote(drawerPharmacy.value.customerId, noteInput.value)
  noteInput.value = ''
}
</script>

<template>
  <div>
    <div class="card module-header" style="display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap;">
      <div>
        <span class="eyebrow">Module 1 — Pharmacy Master Management</span>
        <h2>{{ store.data.pharmacies.value.length }} pharmacies onboarded</h2>
        <p class="muted">Click a row for the full profile and order history</p>
      </div>
      <button class="btn-primary" @click="openCreate">+ Add Pharmacy</button>
    </div>

    <div v-if="toast" style="position:fixed;bottom:24px;right:24px;background:var(--color-primary-dark);color:#fff;padding:10px 16px;border-radius:8px;font-size:13px;box-shadow:var(--shadow-lg);max-width:360px;z-index:120;">{{ toast }}</div>

    <div class="card">
      <div class="toolbar">
        <input class="search-input" v-model="search" placeholder="Search by pharmacy, owner or Customer ID…" />
        <select class="filter-select" v-model="territoryFilter"><option v-for="t in territories" :key="t">{{ t }}</option></select>
        <select class="filter-select" v-model="tierFilter"><option>All</option><option>Platinum</option><option>Gold</option><option>Silver</option><option>Bronze</option></select>
        <span class="result-count">{{ filtered.length }} of {{ store.data.pharmacies.value.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Customer ID</th><th>Pharmacy</th><th>Owner</th><th>Territory / City</th><th>Sales Exec</th><th>Footfall (Daily/Mo.)</th><th>Cat.</th><th>Potential</th><th>OTP</th></tr></thead>
          <tbody>
            <tr v-for="p in filtered" :key="p.customerId" class="clickable" @click="openDrawer(p)">
              <td class="cell-muted">{{ p.customerId }}</td><td class="cell-strong">{{ p.pharmacyName }}</td>
              <td>{{ p.ownerName }}</td><td>{{ p.territory }} <span class="cell-muted">— {{ p.city }}</span></td>
              <td class="cell-muted">{{ p.assignedSalesExec }}</td><td class="cell-muted">{{ p.dailyFootfall }} / {{ p.monthlyFootfall }}</td>
              <td class="cell-muted">{{ p.category }}</td>
              <td><span class="tier-badge" :class="p.potentialClassification">{{ p.potentialClassification }}</span></td>
              <td><span class="otp-check" :class="p.otpVerified?'verified':'pending'"><Icon :name="p.otpVerified?'shield':'alert'" :size="13" />{{ p.otpVerified?'Verified':'Pending' }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <DetailDrawer :open="!!drawerPharmacy" :title="drawerPharmacy?.pharmacyName||''" @close="drawerCustomerId=null">
      <template v-if="drawerPharmacy">
        <div class="eyebrow" style="margin-bottom:10px;">Identity & Licensing</div>
        <dl style="display:grid;grid-template-columns:auto 1fr;gap:8px 16px;margin:0 0 20px;font-size:13px;">
          <dt class="cell-muted">Customer ID</dt><dd style="margin:0" class="cell-strong">{{ drawerPharmacy.customerId }}</dd>
          <dt class="cell-muted">Owner</dt><dd style="margin:0">{{ drawerPharmacy.ownerName }}</dd>
          <dt class="cell-muted">Drug License</dt><dd style="margin:0">{{ drawerPharmacy.drugLicenseNo }}</dd>
          <dt class="cell-muted">Mobile 1</dt><dd style="margin:0">{{ drawerPharmacy.mobile1 }}</dd>
          <dt class="cell-muted">Mobile 2</dt><dd style="margin:0">{{ drawerPharmacy.mobile2||'—' }}</dd>
          <dt class="cell-muted">Email</dt><dd style="margin:0;word-break:break-all">{{ drawerPharmacy.email1 }}</dd>
          <dt class="cell-muted">Address</dt><dd style="margin:0">{{ drawerPharmacy.address }}, {{ drawerPharmacy.city }}</dd>
        </dl>

        <div class="eyebrow" style="margin-bottom:10px;">Account Verification</div>
        <div style="display:flex; align-items:center; gap:12px; margin-bottom:20px; flex-wrap:wrap;">
          <span class="otp-check" :class="drawerPharmacy.otpVerified?'verified':'pending'"><Icon :name="drawerPharmacy.otpVerified?'shield':'alert'" :size="13" />{{ drawerPharmacy.otpVerified?'Activated':'Pending OTP' }}</span>
          <button v-if="!drawerPharmacy.otpVerified" class="filter-select" style="cursor:pointer;" @click="openOtpModal">Verify Account (Dual-OTP)</button>
        </div>

        <div class="eyebrow" style="margin-bottom:10px;">Business Profiling — Field Intelligence</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:10px;">
          <div class="form-group">
            <label>Daily Footfall</label>
            <input class="form-input" type="number" min="0" v-model.number="footfallForm.dailyFootfall" />
          </div>
          <div class="form-group">
            <label>Monthly Footfall</label>
            <input class="form-input" type="number" min="0" v-model.number="footfallForm.monthlyFootfall" />
          </div>
        </div>
        <div style="margin-bottom:20px;">
          <button class="filter-select" style="cursor:pointer;" @click="recalcScore">Recalculate Potential Score</button>
          <span class="cell-muted" style="font-size:12px;margin-left:10px;">Current: {{ drawerPharmacy.category }} / {{ drawerPharmacy.potentialClassification }} ({{ drawerPharmacy.potentialScore }})</span>
        </div>

        <div class="eyebrow" style="margin-bottom:10px;">Field Visit Notes</div>
        <div style="display:flex; gap:8px; margin-bottom:12px;">
          <input class="form-input" v-model="noteInput" placeholder="Log a visit note…" @keyup.enter="submitNote" />
          <button class="filter-select" style="cursor:pointer;" :disabled="!noteInput.trim()" @click="submitNote">Add Note</button>
        </div>
        <div v-if="!visitNotes.length" class="muted" style="font-size:12.5px;margin-bottom:20px;">No visit notes logged yet.</div>
        <div v-else style="margin-bottom:20px;">
          <div v-for="(n,i) in visitNotes" :key="i" class="oos-remarks" style="margin-bottom:8px;">
            <strong style="display:block;font-size:11px;color:var(--color-text-muted);margin-bottom:2px;">{{ n.timestamp }}</strong>
            {{ n.note }}
          </div>
        </div>

        <div class="eyebrow" style="margin-bottom:10px;">Order History ({{ drawerOrders.length }})</div>
        <div v-if="!drawerOrders.length" class="muted" style="font-size:13px;margin-bottom:12px;">No orders in this scenario.</div>
        <table v-else class="data-table" style="margin-bottom:12px;">
          <thead><tr><th>Order No.</th><th>Product</th><th>Value</th></tr></thead>
          <tbody><tr v-for="o in drawerOrders" :key="o.orderNo"><td class="cell-muted">{{ o.orderNo }}</td><td>{{ o.productName }}</td><td class="cell-muted">${{ o.orderValue.toLocaleString() }}</td></tr></tbody>
        </table>
        <div class="cell-strong">Total: ${{ drawerTotalValue.toLocaleString() }}</div>
      </template>
    </DetailDrawer>

    <!-- Dual-OTP verification modal -->
    <DetailDrawer :open="showOtpModal" title="Dual-OTP Account Verification" @close="showOtpModal=false">
      <p class="muted" style="font-size:12.5px;margin:0 0 16px;">Enter the mock 4-digit OTP sent to each registered channel to activate this account.</p>
      <div v-for="f in otpFieldsRequired" :key="f.key" class="form-group">
        <label>{{ f.label }} — {{ f.value }}</label>
        <input class="form-input" maxlength="4" placeholder="4-digit code" v-model="otpInputs[f.key]" />
      </div>
      <button class="btn-primary" :disabled="!otpAllFilled" @click="submitOtp">Verify All &amp; Activate</button>
    </DetailDrawer>

    <!-- Add Pharmacy form -->
    <DetailDrawer :open="showCreate" title="Add Pharmacy" @close="showCreate=false">
      <div class="form-group"><label>Pharmacy Name</label><input class="form-input" v-model="createForm.pharmacyName" /></div>
      <div class="form-group"><label>Owner Name</label><input class="form-input" v-model="createForm.ownerName" /></div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <div class="form-group"><label>Mobile 1</label><input class="form-input" v-model="createForm.mobile1" placeholder="+263 7…" /></div>
        <div class="form-group"><label>Mobile 2 (optional)</label><input class="form-input" v-model="createForm.mobile2" placeholder="+263 7…" /></div>
      </div>
      <div class="form-group"><label>Email 1</label><input class="form-input" v-model="createForm.email1" /></div>
      <div class="form-group"><label>Drug License No.</label><input class="form-input" v-model="createForm.drugLicenseNo" placeholder="MCAZ-XXXX-2026" /></div>
      <div v-if="duplicateMatch" class="oos-remarks" style="margin-bottom:16px;border-color:var(--status-red);">
        ⚠ Possible duplicate: <strong>{{ duplicateMatch.pharmacyName }}</strong> ({{ duplicateMatch.customerId }}) already uses this Drug License No. or Mobile 1.
      </div>
      <div class="form-group"><label>Address</label><input class="form-input" v-model="createForm.address" /></div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <div class="form-group"><label>City</label><input class="form-input" v-model="createForm.city" /></div>
        <div class="form-group"><label>Territory</label>
          <select class="form-select" v-model="createForm.territory"><option v-for="t in TERRITORIES" :key="t">{{ t }}</option></select>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <div class="form-group"><label>Daily Footfall</label><input class="form-input" type="number" min="0" v-model.number="createForm.dailyFootfall" /></div>
        <div class="form-group"><label>Monthly Footfall</label><input class="form-input" type="number" min="0" v-model.number="createForm.monthlyFootfall" /></div>
      </div>
      <button class="btn-primary" :disabled="!createForm.pharmacyName || !createForm.ownerName || !createForm.mobile1 || !createForm.drugLicenseNo || !!duplicateMatch" @click="submitCreate">
        Onboard Pharmacy
      </button>
      <p v-if="duplicateMatch" class="muted" style="font-size:12px;margin-top:8px;">Resolve the duplicate above before onboarding.</p>
    </DetailDrawer>
  </div>
</template>